/**
 * UnifiedHeader - Notebook View Header
 *
 * Combined header with back navigation, title editing,
 * stats display, and action menu.
 *
 * @module components/UnifiedHeader
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';

import {
  ArrowLeft,
  BookOpen,
  CheckCircle2,
  AlertCircle,
  Loader2,
  MoreVertical,
  Edit3,
  RefreshCw,
  Trash2,
  Plus,
  Activity,
  Wrench,
  AlertTriangle,
} from 'lucide-react';

import { SystemStatusModal } from '@/pages/intelligence-studio/components/modals';
import ErrorDetailsDrawer from '@/pages/intelligence-studio/components/errors/ErrorDetailsDrawer';
import NotebookErrorsDrawer from '@/pages/intelligence-studio/components/errors/NotebookErrorsDrawer';
import { normalizeUniversalError, isUIUniversalError } from '@/pages/intelligence-studio/utils/universalError';
import { traceFetch } from '@/pages/intelligence-studio/utils/traceContext';
import { resolveAuthToken } from '@/pages/intelligence-studio/utils/authTokens';
import { getApiBaseUrl } from '../../../config/runtimeUrls';
import { useNetworkStatus } from '../../../contexts/NetworkStatusContext';

/**
 * UnifiedHeader Component
 *
 * @param {Object} props
 * @param {Object} props.notebook - Current notebook object
 * @param {Function} props.onBack - Navigate back to home
 * @param {Function} props.onRefresh - Refresh notebook data
 * @param {Function} props.onRename - Rename notebook (id, newTitle)
 * @param {Function} props.onDelete - Delete notebook
 * @param {Function} props.onNewNotebook - Create new notebook
 * @param {Object} props.quotaStatus - Quota/usage status (for utilization indicator)
 */
const UnifiedHeader = ({
  notebook,
  quotaStatus,
  onBack,
  onRefresh,
  onRename,
  onDelete,
  onNewNotebook,
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState(notebook?.title || '');
  const [showSystemStatus, setShowSystemStatus] = useState(false);
  const [isFixingStuck, setIsFixingStuck] = useState(false);
  const [fixResult, setFixResult] = useState(null);
  // ✅ PHASE 7: Notebook-level aggregated errors drawer
  const [errorsDrawerOpen, setErrorsDrawerOpen] = useState(false);
  const [activeErrorForDrawer, setActiveErrorForDrawer] = useState(null);
  const [errorDetailsOpen, setErrorDetailsOpen] = useState(false);
  const { isOffline } = useNetworkStatus();
  const inputRef = useRef(null);

  // ✅ ENTERPRISE: Detect stuck notebooks (processing for > 2 minutes with no progress)
  const STUCK_THRESHOLD_MS = 2 * 60 * 1000; // 2 minutes
  const isStuck = (() => {
    if (notebook?._isStuck) return true;
    if (notebook?.status !== 'processing') return false;
    const updatedAt = new Date(notebook?.updatedAt || notebook?.createdAt);
    const ageMs = Date.now() - updatedAt.getTime();
    return ageMs > STUCK_THRESHOLD_MS;
  })();

  const hasLoadError = notebook?._loadError || notebook?._pollError;
  const errorMessage = notebook?._errorMessage || notebook?._stuckMessage;

  // ✅ ENTERPRISE: Fix stuck vault handler
  const handleFixStuckVault = useCallback(async () => {
    if (!notebook?.id || isFixingStuck) return;

    setIsFixingStuck(true);
    setFixResult(null);

    try {
      console.log(`🔧 [UnifiedHeader] Fixing stuck vault: ${notebook.id}`);

      const token = resolveAuthToken();
      const apiBase = String(getApiBaseUrl() || '').replace(/\/+$/, '');
      const response = await traceFetch(
        `${apiBase}/intelligence-studio/notebooks/${notebook.id}/finalize`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ force: true }),
        },
        {
          apiName: 'notebook.finalize',
          pipelineStage: 'http',
          notebookId: notebook.id,
        }
      );

      const result = await response.json();

      if (result?.finalized || result?.success) {
        setFixResult({ success: true, message: 'Vault fixed successfully! Refreshing...' });
        // Refresh the notebook after a short delay
        setTimeout(() => {
          onRefresh?.();
          setFixResult(null);
        }, 1500);
        return;
      }

      const files = Array.isArray(notebook?.files) ? notebook.files : [];
      const reprocessTargets = files
        .map(f => ({
          fileId: f?.fileId || f?.id,
          status: String(f?.status || '').toLowerCase(),
        }))
        .filter(f => f.fileId && ['pending', 'uploading', 'queued', 'processing', 'restoring'].includes(f.status));

      if (reprocessTargets.length > 0) {
        const reprocessResults = await Promise.allSettled(
          reprocessTargets.map(({ fileId }) =>
            traceFetch(
              `${apiBase}/intelligence-studio/notebooks/${notebook.id}/files/${fileId}/reprocess`,
              {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ reason: 'stuck_vault_fix', tryCascade: true }),
              },
              {
                apiName: 'notebook.file.reprocess',
                pipelineStage: 'http',
                notebookId: notebook.id,
                fileId,
              }
            )
          )
        );
        const okCount = reprocessResults.filter(r => r.status === 'fulfilled').length;
        if (okCount > 0) {
          setFixResult({
            success: true,
            message: `Re-queued ${okCount} file${okCount === 1 ? '' : 's'} for processing. Refreshing...`,
          });
          setTimeout(() => {
            onRefresh?.();
            setFixResult(null);
          }, 1500);
          return;
        }
      }

      setFixResult({
        success: false,
        message:
          result?.reason ||
          result?.message ||
          'Could not fix vault. Please try again or contact support.',
      });
    } catch (err) {
      console.error('❌ [UnifiedHeader] Fix stuck vault failed:', err);
      setFixResult({
        success: false,
        message: `Error: ${err.message}. Please try refreshing the page.`,
      });
    } finally {
      setIsFixingStuck(false);
    }
  }, [notebook?.id, isFixingStuck, onRefresh]);

  const files = notebook?.files || [];
  const filesCount = files.length;
  const cacheRestoredFiles = files.filter(f => f?.restoredFromCache === true);
  const cacheRestoredCount = cacheRestoredFiles.length;
  const cacheRestoredTooltip =
    cacheRestoredCount > 0
      ? [
          `Restored from cache: ${cacheRestoredCount} file${cacheRestoredCount === 1 ? '' : 's'}`,
          ...cacheRestoredFiles.slice(0, 5).map(f => {
            const name = f?.originalName || f?.name || f?.filename || f?.fileId || 'File';
            const at = f?.restoredAt ? ` @ ${f.restoredAt}` : '';
            const hash = f?.restoredHash ? ` (${String(f.restoredHash).slice(0, 12)}…)` : '';
            return `- ${name}${at}${hash}`;
          }),
          cacheRestoredCount > 5 ? `… +${cacheRestoredCount - 5} more` : null,
        ]
          .filter(Boolean)
          .join('\n')
      : '';

  // Calculate file type breakdown
  const fileTypes = {};
  files.forEach(f => {
    const ext = (f.originalName || f.name || '').split('.').pop()?.toLowerCase() || 'file';
    fileTypes[ext] = (fileTypes[ext] || 0) + 1;
  });

  const typeBreakdown = Object.entries(fileTypes)
    .map(([ext, count]) => {
      const label =
        ext === 'pdf'
          ? 'PDF'
          : ext === 'docx' || ext === 'doc'
            ? 'Word'
            : ext === 'xlsx' || ext === 'xls' || ext === 'csv'
              ? 'Excel'
              : ext === 'pptx' || ext === 'ppt'
                ? 'PPT'
                : ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(ext)
                  ? 'IMG'
                  : ext === 'txt' || ext === 'md'
                    ? 'TXT'
                    : ext.toUpperCase();
      return `${count} ${label}`;
    })
    .join(', ');

  // Focus input when editing
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  // Close menu when clicking outside
  useEffect(() => {
    if (menuOpen) {
      const handler = () => setMenuOpen(false);
      document.addEventListener('click', handler);
      return () => document.removeEventListener('click', handler);
    }
  }, [menuOpen]);

  const handleSaveTitle = async () => {
    if (isOffline) {
      setIsEditing(false);
      return;
    }
    if (editTitle.trim() && editTitle !== notebook?.title && onRename && notebook?.id) {
      await onRename(notebook.id, editTitle.trim());
    }
    setIsEditing(false);
  };

  // ✅ Canonical trust contract (server-side source of truth)
  const trustStateRaw = String(notebook?.userTrustSummary?.state || '').toLowerCase();
  const trustState = trustStateRaw === 'reading' ? 'processing' : trustStateRaw;
  const trustBadge = notebook?.userTrustSummary?.badge || null;
  const analyzed =
    typeof notebook?.userTrustSummary?.analyzed === 'number'
      ? notebook.userTrustSummary.analyzed
      : null;
  const total =
    typeof notebook?.userTrustSummary?.total === 'number' ? notebook.userTrustSummary.total : null;
  const vaultPreviewBadge = notebook?.vaultPreview?.badge || null;
  const vaultPreviewTooltip = notebook?.vaultPreview?.tooltip || '';

  const notebookStatus = String(notebook?.status || '').toLowerCase();
  const isFailed = notebookStatus === 'failed' || notebookStatus === 'error';
  const notebookUiErrors = Array.isArray(notebook?._uiErrors) ? notebook._uiErrors : [];
  const extraNotebookErrors = [
    notebook?._lastSseError || null,
    notebook?._lastPollError || null,
  ].filter(Boolean);
  const fileErrorItems = (() => {
    const list = [];
    for (const f of files) {
      const fid = f?.fileId || f?.id || null;
      const name = f?.originalName || f?.name || f?.filename || (fid ? `File ${fid}` : 'File');
      const st = String(f?.status || '').toLowerCase();
      const raw =
        f?.lastError ||
        f?.errorDetails ||
        (f?.error === true ? f : null) ||
        (st === 'failed' ? f?.failure || f?.processingError : null);
      if (!raw) continue;
      const uiErr = isUIUniversalError(raw)
        ? raw
        : normalizeUniversalError(raw, {
            source: 'sse',
            fallbackCode: 'FILE_ERROR',
            fallbackMessage: 'File processing failed',
            raw,
          });
      list.push({ scope: 'file', fileId: fid ? String(fid) : null, fileName: name, status: st, error: uiErr });
    }
    return list;
  })();
  const notebookErrorItems = [...notebookUiErrors, ...extraNotebookErrors].map(e => ({
    scope: 'notebook',
    fileId: null,
    fileName: notebook?.title || 'Notebook',
    status: String(notebook?.status || '').toLowerCase(),
    error: isUIUniversalError(e)
      ? e
      : normalizeUniversalError(e, { source: 'sse', fallbackCode: 'NOTEBOOK_ERROR', fallbackMessage: 'Notebook error' }),
  }));
  const allErrorItems = (() => {
    const combined = [...notebookErrorItems, ...fileErrorItems];
    const seen = new Set();
    const out = [];
    for (const it of combined) {
      const k = `${it.scope}:${it.fileId || 'nb'}:${it.error?.code || ''}:${it.error?.message || ''}`;
      if (seen.has(k)) continue;
      seen.add(k);
      out.push(it);
    }
    return out;
  })();
  const hasSevereErrors =
    allErrorItems.length > 0 ||
    files.some(f => {
      const st = String(f?.status || '').toLowerCase();
      return st === 'failed' || st === 'blocked_low_quality';
    });
  const hasActiveProcessing = files.some(f => {
    const st = String(f?.status || '').toLowerCase();
    return (
      st === 'uploading' ||
      st === 'queued' ||
      st === 'processing' ||
      st === 'analyzing' ||
      st.startsWith('processing')
    );
  });
  const isReadyFromTrust = trustState === 'trusted' || trustState === 'readable';
  const isLowQuality = trustState === 'low_quality';
  const isProcessing =
    trustState === 'processing' ||
    (!isReadyFromTrust && !isLowQuality && !isFailed && hasActiveProcessing);

  // ✅ Victory moment: vault trust flip should feel like a win (subtle, non-disruptive).
  const prevTrustRef = useRef(trustState);
  const [trustJustUnlocked, setTrustJustUnlocked] = useState(false);
  useEffect(() => {
    const prev = prevTrustRef.current;
    prevTrustRef.current = trustState;
    if (!prev || prev === trustState) return;
    const becameReady =
      (trustState === 'trusted' || trustState === 'readable') &&
      !['trusted', 'readable'].includes(prev);
    if (!becameReady) return;
    setTrustJustUnlocked(true);
    const t = setTimeout(() => setTrustJustUnlocked(false), 1200);
    t?.unref?.();
    return () => clearTimeout(t);
  }, [trustState]);

  // ✅ Quota utilization ring (Cursor-style)
  const quotaOk = quotaStatus?.ok === true;
  const qUsage = quotaOk ? quotaStatus?.usage : null;
  const qPolicy = quotaOk ? quotaStatus?.policy : null;
  const qWindow = quotaOk ? quotaStatus?.window : null;

  const jobsUsed = typeof qUsage?.dailyJobsUsed === 'number' ? qUsage.dailyJobsUsed : null;
  const jobsLimit = typeof qUsage?.dailyJobsLimit === 'number' ? qUsage.dailyJobsLimit : null;
  const costUsed = typeof qUsage?.dailyCostUsed === 'number' ? qUsage.dailyCostUsed : null;
  const costLimit = typeof qUsage?.dailyCostLimit === 'number' ? qUsage.dailyCostLimit : null;
  const resetsAt = qWindow?.resetsAt || null;

  const jobsUnlimited = !jobsLimit || jobsLimit <= 0 || jobsLimit >= 90_000;
  const costUnlimited = !costLimit || costLimit <= 0 || costLimit >= 90_000_000;
  const jobsPct =
    jobsUnlimited || jobsUsed === null
      ? 0
      : Math.max(0, Math.min(100, Math.round((jobsUsed / Math.max(1, jobsLimit)) * 100)));
  const ringColor = jobsPct >= 95 ? '#ef4444' : jobsPct >= 80 ? '#f59e0b' : '#22c55e';
  const quotaTitle = quotaOk
    ? [
        `Quota (Intelligence Studio)`,
        qPolicy?.tier ? `Tier: ${qPolicy.tier}` : null,
        jobsUsed !== null ? `Jobs today: ${jobsUsed}/${jobsUnlimited ? '∞' : jobsLimit}` : null,
        costUsed !== null
          ? `Cost units today: ${costUsed}/${costUnlimited ? '∞' : costLimit}`
          : null,
        resetsAt ? `Resets at (UTC): ${resetsAt}` : null,
      ]
        .filter(Boolean)
        .join('\n')
    : 'Quota status unavailable';

  return (
    <div className="flex-shrink-0 bg-white dark:bg-neutral-900 border-b border-slate-200 dark:border-neutral-700">
      {isOffline && (
        <div className="px-4 py-2 text-xs bg-amber-50 text-amber-800 border-b border-amber-200 dark:bg-amber-900/20 dark:text-amber-200 dark:border-amber-800">
          <strong>Reconnecting...</strong> You are offline. Saving is disabled until the connection returns.
        </div>
      )}
      {/* ✅ PHASE 7: Details drawer for a selected error */}
      <ErrorDetailsDrawer
        open={errorDetailsOpen}
        onClose={() => setErrorDetailsOpen(false)}
        error={activeErrorForDrawer}
        title="Error details"
      />
      {/* ✅ PHASE 7: Aggregated errors drawer */}
      <NotebookErrorsDrawer
        open={errorsDrawerOpen}
        onClose={() => setErrorsDrawerOpen(false)}
        items={allErrorItems}
        onSelect={it => {
          setActiveErrorForDrawer(it?.error || null);
          setErrorDetailsOpen(true);
        }}
      />
      <div className="px-4 flex items-center justify-between h-12">
        {/* Left side: Back + Title + Stats */}
        <div className="flex items-center gap-3 flex-1 min-w-0">
          {/* Back button */}
          <button
            onClick={onBack}
            className="flex items-center gap-1.5 px-2 py-1 text-slate-500 dark:text-neutral-400 hover:bg-slate-100 dark:hover:bg-neutral-800 rounded-lg transition-colors flex-shrink-0"
          >
            <ArrowLeft className="h-4 w-4" />
            <span className="text-xs hidden sm:inline">All Notebooks</span>
          </button>

          {/* Divider */}
          <div className="w-px h-5 bg-slate-200 dark:bg-neutral-700 flex-shrink-0" />

          {/* Title + Stats */}
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <div className="p-1.5 bg-amber-100 dark:bg-amber-900/30 rounded-lg flex-shrink-0">
              <BookOpen className="h-4 w-4 text-amber-600 dark:text-amber-400" />
            </div>
            <div className="min-w-0 flex-1">
              {isEditing ? (
                <input
                  ref={inputRef}
                  type="text"
                  value={editTitle}
                  onChange={e => setEditTitle(e.target.value)}
                  onBlur={handleSaveTitle}
                  onKeyDown={e => {
                    if (e.key === 'Enter') handleSaveTitle();
                    if (e.key === 'Escape') setIsEditing(false);
                  }}
                  disabled={isOffline}
                  title={isOffline ? 'Offline: reconnect to save changes' : undefined}
                  className="w-full px-2 py-0.5 text-sm font-medium bg-white dark:bg-neutral-800 border-2 border-amber-400 rounded outline-none"
                />
              ) : (
                <h2 className="font-medium text-sm text-slate-900 dark:text-white truncate">
                  {notebook?.title || 'Untitled'}
                </h2>
              )}
              <p className="text-[10px] text-slate-500 dark:text-neutral-400 truncate">
                {filesCount} source{filesCount !== 1 ? 's' : ''}
                {/* ✅ ENTERPRISE V2: REMOVED "• X/Y analyzed" display - misleading when X=0 */}
                {vaultPreviewBadge && (
                  <span title={vaultPreviewTooltip}> • {vaultPreviewBadge}</span>
                )}
                {cacheRestoredCount > 0 && (
                  <span
                    className="ml-1"
                    title={cacheRestoredTooltip}
                  >
                    • Restored from cache
                  </span>
                )}
                {typeBreakdown && <span className="ml-1">({typeBreakdown})</span>}
              </p>
            </div>
          </div>
        </div>

        {/* Right side: Status + Actions */}
        <div className="flex items-center gap-2 flex-shrink-0">
          {/* ✅ PHASE 7: Error badge (global) */}
          {allErrorItems.length > 0 && (
            <button
              type="button"
              onClick={() => setErrorsDrawerOpen(true)}
              className="flex items-center gap-1 px-2 py-0.5 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 text-[10px] rounded-full hover:bg-red-200 dark:hover:bg-red-900/50 transition-colors"
              title="View errors"
              data-testid="istudio-header-error-badge"
            >
              <AlertCircle className="h-3 w-3" />
              {allErrorItems.length} error{allErrorItems.length === 1 ? '' : 's'}
            </button>
          )}
          {/* Notebook trust badge (canonical) */}
          {isFailed || hasLoadError ? (
            <span className="flex items-center gap-1 px-2 py-0.5 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 text-[10px] rounded-full">
              <AlertCircle className="h-3 w-3" />
              {hasLoadError ? 'Error' : 'Failed'}
            </span>
          ) : isStuck ? (
            <span className="flex items-center gap-1 px-2 py-0.5 bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400 text-[10px] rounded-full animate-pulse">
              <AlertTriangle className="h-3 w-3" />
              Stuck
            </span>
          ) : isLowQuality ? (
            <span className="flex items-center gap-1 px-2 py-0.5 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 text-[10px] rounded-full">
              <AlertCircle className="h-3 w-3" />
              {trustBadge || 'Low Quality'}
            </span>
          ) : isReadyFromTrust ? (
            <span
              className={`flex items-center gap-1 px-2 py-0.5 text-[10px] rounded-full ${
                trustState === 'trusted'
                  ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                  : 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200'
              } ${trustJustUnlocked ? 'ring-2 ring-green-300 dark:ring-green-700 animate-pulse' : ''}`}
            >
              <CheckCircle2 className="h-3 w-3" />
              {trustBadge || (trustState === 'trusted' ? 'Trusted' : 'Readable')}
            </span>
          ) : (
            <span className="flex items-center gap-1 px-2 py-0.5 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 text-[10px] rounded-full">
              <Loader2 className="h-3 w-3 animate-spin" />
              {isProcessing ? trustBadge || 'Processing' : 'Pending'}
            </span>
          )}

          {/* ✅ ENTERPRISE: Fix Stuck Vault button */}
          {(isStuck || hasLoadError) && (
            <button
              onClick={handleFixStuckVault}
              disabled={isFixingStuck}
              className="flex items-center gap-1.5 px-2.5 py-1 bg-orange-600 text-white text-xs rounded-lg hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              title={errorMessage || 'Attempt to fix this stuck vault'}
            >
              {isFixingStuck ? (
                <>
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  Fixing...
                </>
              ) : (
                <>
                  <Wrench className="h-3.5 w-3.5" />
                  Fix Vault
                </>
              )}
            </button>
          )}

          {/* Fix result toast */}
          {fixResult && (
            <div
              className={`absolute top-full left-1/2 -translate-x-1/2 mt-2 px-3 py-2 rounded-lg text-xs shadow-lg z-50 max-w-xs ${
                fixResult.success
                  ? 'bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-200 border border-green-200 dark:border-green-800'
                  : 'bg-red-100 dark:bg-red-900/50 text-red-800 dark:text-red-200 border border-red-200 dark:border-red-800'
              }`}
            >
              {fixResult.message}
            </div>
          )}

          {/* ✅ Utilization indicator (quota) */}
          <button
            type="button"
            onClick={() => setShowSystemStatus(true)}
            className="group relative flex items-center justify-center w-8 h-8 rounded-full hover:bg-slate-100 dark:hover:bg-neutral-800 transition-colors"
            title={quotaTitle}
            aria-label="Quota usage"
          >
            <div
              className="relative w-6 h-6 rounded-full"
              style={{
                background: quotaOk
                  ? `conic-gradient(${ringColor} ${jobsPct}%, rgba(148,163,184,0.35) 0)`
                  : 'rgba(148,163,184,0.35)',
              }}
            >
              <div className="absolute inset-[3px] bg-white dark:bg-neutral-900 rounded-full flex items-center justify-center">
                <span className="text-[10px] font-semibold text-slate-700 dark:text-neutral-200 tabular-nums">
                  {jobsUnlimited ? '∞' : `${jobsPct}%`}
                </span>
              </div>
            </div>
          </button>

          {/* Three dots menu */}
          <div className="relative">
            <button
              onClick={e => {
                e.stopPropagation();
                setMenuOpen(!menuOpen);
              }}
              className="p-1.5 hover:bg-slate-100 dark:hover:bg-neutral-800 rounded-lg transition-colors"
            >
              <MoreVertical className="h-4 w-4 text-slate-500 dark:text-neutral-400" />
            </button>

            {menuOpen && (
              <div className="absolute right-0 top-full mt-1 bg-white dark:bg-neutral-800 rounded-lg border border-slate-200 dark:border-neutral-700 shadow-lg z-50 py-1 min-w-[140px]">
                <button
                  onClick={e => {
                    e.stopPropagation();
                    setMenuOpen(false);
                    setShowSystemStatus(true);
                  }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 dark:text-neutral-200 hover:bg-slate-100 dark:hover:bg-neutral-700"
                >
                  <Activity className="h-4 w-4" />
                  System Status
                </button>
                <button
                  onClick={e => {
                    e.stopPropagation();
                    setMenuOpen(false);
                    setEditTitle(notebook?.title || '');
                    setIsEditing(true);
                  }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 dark:text-neutral-200 hover:bg-slate-100 dark:hover:bg-neutral-700"
                >
                  <Edit3 className="h-4 w-4" />
                  Rename
                </button>
                <button
                  onClick={e => {
                    e.stopPropagation();
                    setMenuOpen(false);
                    onRefresh?.();
                  }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 dark:text-neutral-200 hover:bg-slate-100 dark:hover:bg-neutral-700"
                >
                  <RefreshCw className="h-4 w-4" />
                  Refresh
                </button>
                <div className="border-t border-slate-200 dark:border-neutral-700 my-1" />
                <button
                  onClick={e => {
                    e.stopPropagation();
                    setMenuOpen(false);
                    onDelete?.(notebook);
                  }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
                >
                  <Trash2 className="h-4 w-4" />
                  Delete
                </button>
              </div>
            )}
          </div>

          {/* New Notebook button */}
          <button
            onClick={onNewNotebook}
            className="flex items-center gap-1.5 px-2.5 py-1.5 bg-amber-600 text-white text-xs rounded-lg hover:bg-amber-700 transition-colors"
          >
            <Plus className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">New</span>
          </button>
        </div>
      </div>

      {/* ✅ PHASE 7: Severe error banner under header */}
      {hasSevereErrors && (
        <div className="px-4 pb-3">
          <div className="px-3 py-2 rounded-xl border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 flex items-center justify-between gap-3">
            <div className="text-xs text-red-800 dark:text-red-200">
              <strong>Some sources have errors.</strong> Ingestion or analysis may be incomplete.
            </div>
            <button
              className="text-xs px-2 py-1 rounded-lg bg-red-600 hover:bg-red-700 text-white"
              onClick={() => setErrorsDrawerOpen(true)}
              data-testid="istudio-header-view-errors"
            >
              View all errors
            </button>
          </div>
        </div>
      )}

      {/* ✅ Enterprise diagnostics */}
      <SystemStatusModal open={showSystemStatus} onClose={() => setShowSystemStatus(false)} />
    </div>
  );
};

export default UnifiedHeader;
